import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import Logo from "@/components/landing/Logo";
import { Loader2, ArrowRight, Check, ShieldCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLANS = [
  { key: "solo", name: "Solo Fleet", tag: "1–3 trucks", monthly: 349, annual: 279, lookup: { monthly: "solo_monthly", annual: "solo_annual" } },
  { key: "growth", name: "Growth Contractor", tag: "4–12 trucks", monthly: 749, annual: 599, popular: true, lookup: { monthly: "growth_monthly", annual: "growth_annual" } },
  { key: "enterprise", name: "Enterprise Fleet", tag: "13+ trucks", monthly: 1499, annual: 1199, lookup: { monthly: "enterprise_monthly", annual: "enterprise_annual" } },
];

const inputCls =
  "w-full rounded-md border border-white/[0.1] bg-[#0B0D10] px-4 py-3 text-sm text-slate-100 placeholder:text-slate-600 outline-none transition-colors duration-200 focus:border-[#F25F22]/60 focus:ring-2 focus:ring-[#F25F22]/20";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  return String(detail);
}

export default function Signup() {
  const [params] = useSearchParams();
  const initialPlan = PLANS.find((p) => p.key === params.get("plan")) || PLANS[1];
  const [plan, setPlan] = useState(initialPlan.key);
  const [annual, setAnnual] = useState(params.get("billing") !== "monthly");
  const [form, setForm] = useState({ company_name: "", trade: "hvac", email: "", oncall_phone: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const selected = PLANS.find((p) => p.key === plan);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data: account } = await axios.post(`${API}/signup`, {
        ...form,
        oncall_phone: form.oncall_phone || null,
        plan: plan,
        ref: params.get("ref") || undefined,
      });
      const lookup_key = selected.lookup[annual ? "annual" : "monthly"];
      const { data: checkout } = await axios.post(`${API}/billing/checkout`, {
        lookup_key,
        user_id: account.user_id,
        customer_id: account.customer_id,
        origin_url: window.location.origin,
      });
      window.location.href = checkout.checkout_url;
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B0D10] px-5 py-14 sm:px-8" data-testid="signup-page">
      <div className="mx-auto max-w-4xl">
        <div className="flex items-center justify-center gap-2.5">
          <Logo size={34} />
          <span className="font-display text-lg font-bold text-slate-50">Backfill</span>
        </div>
        <h1 className="mt-6 text-center font-display text-2xl font-bold text-slate-50 sm:text-3xl">
          Start recovering revenue tonight
        </h1>
        <p className="mt-2 text-center text-sm text-slate-400">
          Create your account, pick a plan, and you're live within 48 hours.
        </p>
        {params.get("ref") && (
          <div data-testid="signup-referral-banner" className="mx-auto mt-5 max-w-md rounded-lg border border-emerald-500/30 bg-emerald-500/[0.06] px-4 py-3 text-center text-sm text-emerald-300">
            A fellow contractor referred you — welcome aboard.
          </div>
        )}

        <div className="mt-10 grid gap-8 lg:grid-cols-[1fr_360px]">
          <form onSubmit={submit} data-testid="signup-form" className="rounded-xl border border-white/[0.1] bg-[#12161F] p-7">
            <div className="space-y-4">
              <div>
                <label htmlFor="su-company" className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-400">Company name</label>
                <input id="su-company" required data-testid="signup-company-input" value={form.company_name} onChange={set("company_name")} placeholder="Apex Heating & Air" className={inputCls} />
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="su-trade" className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-400">Trade</label>
                  <select id="su-trade" data-testid="signup-trade-select" value={form.trade} onChange={set("trade")} className={inputCls}>
                    {["hvac", "plumbing", "electrical", "roofing", "general"].map((t) => (
                      <option key={t} value={t}>{t[0].toUpperCase() + t.slice(1)}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="su-oncall" className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-400">On-call phone (optional)</label>
                  <input id="su-oncall" data-testid="signup-oncall-input" value={form.oncall_phone} onChange={set("oncall_phone")} placeholder="+1 555 123 4567" className={inputCls} />
                </div>
              </div>
              <div>
                <label htmlFor="su-email" className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-400">Work email</label>
                <input id="su-email" type="email" required data-testid="signup-email-input" value={form.email} onChange={set("email")} placeholder="you@yourcompany.com" className={inputCls} />
              </div>
              <div>
                <label htmlFor="su-password" className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-400">Portal password</label>
                <input id="su-password" type="password" required minLength={10} data-testid="signup-password-input" value={form.password} onChange={set("password")} placeholder="10+ characters" className={inputCls} />
              </div>
            </div>
            {error && <p data-testid="signup-error" className="mt-4 text-sm text-red-400">{error}</p>}
            <button
              type="submit"
              disabled={loading}
              data-testid="signup-submit-button"
              className="group mt-6 flex w-full items-center justify-center gap-2 rounded-md bg-[#F25F22] py-3.5 text-sm font-semibold text-white transition-all hover:bg-[#FF7539] hover:shadow-[0_0_28px_rgba(242,95,34,0.45)] disabled:opacity-60"
            >
              {loading ? (
                <><Loader2 className="h-4 w-4 animate-spin" /> Preparing secure checkout…</>
              ) : (
                <>Continue to Payment <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></>
              )}
            </button>
            <p className="mt-4 text-center text-xs text-slate-500">
              Secure checkout by Stripe · Test mode — no real charges
            </p>
          </form>

          <div>
            <div data-testid="billing-toggle" className="mb-4 flex rounded-full border border-white/[0.1] bg-[#12161F] p-1">
              {[{ label: "Monthly", v: false }, { label: "Annual · save 20%", v: true }].map((o) => (
                <button
                  key={o.label}
                  type="button"
                  data-testid={o.v ? "signup-toggle-annual" : "signup-toggle-monthly"}
                  onClick={() => setAnnual(o.v)}
                  className={`flex-1 rounded-full px-3 py-2 text-xs font-semibold transition-all ${annual === o.v ? "bg-[#F25F22] text-white" : "text-slate-400 hover:text-slate-200"}`}
                >
                  {o.label}
                </button>
              ))}
            </div>
            <div className="space-y-2.5">
              {PLANS.map((p) => (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => setPlan(p.key)}
                  data-testid={`signup-plan-${p.key}`}
                  className={`w-full rounded-lg border p-4 text-left transition-all ${
                    plan === p.key
                      ? "border-[#F25F22]/60 bg-[#F25F22]/[0.07]"
                      : "border-white/[0.08] bg-[#12161F] hover:border-white/[0.2]"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-display text-sm font-semibold text-slate-50">
                        {p.name}
                        {p.popular && <span className="ml-2 rounded-full bg-[#F25F22] px-2 py-0.5 font-mono text-[9px] uppercase tracking-wider text-white">Popular</span>}
                      </p>
                      <p className="mt-0.5 text-xs text-slate-500">{p.tag}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-display text-lg font-bold text-slate-50">${annual ? p.annual : p.monthly}<span className="text-xs font-normal text-slate-500">/mo</span></p>
                      {annual && <p className="font-mono text-[10px] text-slate-500">billed annually</p>}
                    </div>
                  </div>
                </button>
              ))}
            </div>
            <p className="mt-4 flex items-start gap-2 rounded-lg border border-emerald-400/25 bg-emerald-400/[0.05] p-3.5 text-xs leading-relaxed text-emerald-300">
              <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" />
              Growth plan includes the 90-day revenue guarantee: recover your fee or we work free.
            </p>
            <ul className="mt-4 space-y-2 text-xs text-slate-500">
              {["Instant branded text-back on missed calls", "Smart qualification and emergency escalation", "Weekly recovered-revenue report", "Your own customer portal"].map((f) => (
                <li key={f} className="flex items-center gap-2"><Check className="h-3.5 w-3.5 text-[#F25F22]" />{f}</li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
